// src/config/environment.js
/**
 * Configuración centralizada de variables de entorno
 * Soporta 3 entornos: development, qa, production
 */

// Verificar que las variables de entorno estén disponibles
const requiredEnvVars = [
  "VITE_API_BASE_URL",
  "VITE_API_V1_BASE_URL",
  "VITE_API_V2_BASE_URL",
  "VITE_API_AUTH_URL",
  "VITE_API_BIENES_URL",
  "VITE_API_USERS_URL",
];

// Verificar variables requeridas
const missingVars = requiredEnvVars.filter(
  (varName) => !import.meta.env[varName]
);
if (missingVars.length > 0) {
  console.warn("⚠️ Missing environment variables:", missingVars);
}

// Función para detectar el entorno automáticamente
const detectEnvironment = () => {
  // 1. Prioridad: Variable explícita VITE_APP_ENV
  const envFromVite = import.meta.env.VITE_APP_ENV;
  if (envFromVite) {
    return envFromVite;
  }

  // 2. Fallback: Usar el MODE de Vite directamente
  const mode = import.meta.env.MODE;
  return mode || "development";
};

// Configuración de la API
export const API_CONFIG = {
  // URLs base
  BASE_URL: import.meta.env.VITE_API_BASE_URL || "http://localhost:5000",
  V1_BASE_URL:
    import.meta.env.VITE_API_V1_BASE_URL || "http://localhost:5000/api/v1",
  V2_BASE_URL:
    import.meta.env.VITE_API_V2_BASE_URL || "http://localhost:5000/api/v2",

  // URLs específicas
  AUTH_URL: import.meta.env.VITE_API_AUTH_URL || "http://localhost:5000",
  BIENES_URL:
    import.meta.env.VITE_API_BIENES_URL ||
    "http://localhost:5000/api/v2/bienes",
  USERS_URL:
    import.meta.env.VITE_API_USERS_URL || "http://localhost:5000/api/v1/users",

  // Endpoints específicos construidos dinámicamente
  ENDPOINTS: {
    // Auth
    LOGIN: "/login",

    // Users v1
    SIGNUP: "/sign_up_v2",
    VERIFY_CODE: "/verifyCode",
    SEND_VERIFY_CODE: "/send_verifyCode",

    // Bienes v2
    QUOTES: "/quotes",
    PRODUCTS: "/products",
    FILTERS: "/products/filters",
    PRODUCT_BY_SLUG: "/products/slug",
    POPULAR_CATEGORIES: "/products/categories/popular",

    // 🌟 Sistema de Favoritos v${import.meta.env.VITE_APP_VERSION || "2.1.0"}
    FAVORITES: "/favorites",
    FAVORITES_BULK: "/favorites/bulk",
    FAVORITES_CHECK: "/favorites/check/{productId}",
    FAVORITES_BATCH_CHECK: "/favorites/batch/check",
    FAVORITES_TOGGLE: "/favorites/{productId}/toggle",
    FAVORITES_STATS: "/favorites/stats",
    FAVORITES_POPULAR: "/favorites/popular",
    FAVORITES_CLEANUP: "/favorites/cleanup",

    // Status update
    QUOTE_STATUS_PRIORITY: "/quotes/{id}/status-priority",
  },
};

// Configuración de la aplicación
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || "Tattersall Venta Usados",
  VERSION: import.meta.env.VITE_APP_VERSION || "2.1.0",
  ENV: detectEnvironment(),
  IS_DEVELOPMENT: detectEnvironment() === "development",
  IS_QA: detectEnvironment() === "qa",
  IS_PRODUCTION: detectEnvironment() === "production",

  // URLs por entorno para referencia
  ENVIRONMENT_URLS: {
    development: "http://localhost:5000",
    qa: "https://api.tattersallgestiondeactivos.cl",
    production: "https://api.tattersallgda.cl",
  },

  // 🔗 URLs del panel administrativo (web_client viejo) por entorno.
  // Se usa para redirigir al admin al detalle de un lote con la sesión lista.
  ADMIN_APP_URLS: {
    development:
      import.meta.env.VITE_ADMIN_APP_URL || "http://localhost:3000",
    qa:
      import.meta.env.VITE_ADMIN_APP_URL ||
      "https://tattersallgestiondeactivos.cl",
    production:
      import.meta.env.VITE_ADMIN_APP_URL || "https://tattersallgda.cl",
  },

  // 🎯 Configuración de Google Tag Manager por ambiente
  GTM: {
    // GTM ID por ambiente (usa variables de entorno o valores por defecto)
    ID: import.meta.env.VITE_GTM_ID || (detectEnvironment() === "production" ? "GTM-NS9KD9BG" : null),
    ENABLED: import.meta.env.VITE_GTM_ENABLED === "true" || detectEnvironment() === "production",
    
    // IDs sugeridos por ambiente (configurar en .env)
    ENVIRONMENT_IDS: {
      development: null, // No usar GTM en desarrollo
      qa: import.meta.env.VITE_GTM_ID_QA || null, // Usar contenedor separado para QA
      production: import.meta.env.VITE_GTM_ID_PROD || "GTM-NS9KD9BG",
    },
  },
};

// Función helper para construir URLs completas
export const buildApiUrl = (baseUrl, endpoint, params = {}) => {
  let url = `${baseUrl}${endpoint}`;

  // Reemplazar parámetros en la URL (ej: {id} -> 123)
  Object.entries(params).forEach(([key, value]) => {
    url = url.replace(`{${key}}`, value);
  });

  return url;
};

// URLs pre-construidas para facilitar el uso
export const API_URLS = {
  // Auth
  LOGIN: `${API_CONFIG.AUTH_URL}${API_CONFIG.ENDPOINTS.LOGIN}`,

  // Users
  SIGNUP: `${API_CONFIG.USERS_URL}${API_CONFIG.ENDPOINTS.SIGNUP}`,
  VERIFY_CODE: `${API_CONFIG.USERS_URL}${API_CONFIG.ENDPOINTS.VERIFY_CODE}`,
  SEND_VERIFY_CODE: `${API_CONFIG.USERS_URL}${API_CONFIG.ENDPOINTS.SEND_VERIFY_CODE}`,

  // Bienes
  QUOTES: `${API_CONFIG.BIENES_URL}${API_CONFIG.ENDPOINTS.QUOTES}`,
  PRODUCTS: `${API_CONFIG.BIENES_URL}${API_CONFIG.ENDPOINTS.PRODUCTS}`,
  FILTERS: `${API_CONFIG.BIENES_URL}${API_CONFIG.ENDPOINTS.FILTERS}`,
  PRODUCT_BY_SLUG: `${API_CONFIG.BIENES_URL}${API_CONFIG.ENDPOINTS.PRODUCT_BY_SLUG}`,
};

// Función para obtener URL de actualización de estado de cotización
export const getQuoteStatusUrl = (quoteId) => {
  return buildApiUrl(
    API_CONFIG.BIENES_URL,
    API_CONFIG.ENDPOINTS.QUOTE_STATUS_PRIORITY,
    { id: quoteId }
  );
};

/**
 * Construye la URL del panel administrativo (web_client) para abrir el detalle
 * de un lote con la sesión iniciada. Reutiliza el flujo existente del web_client:
 * `/login?token=<jwt>&redirectUrl=/lots/:lotId` → valida el token vía
 * `api/v1/users/verify`, guarda la sesión y redirige al detalle del lote.
 *
 * @param {number|string} lotId - ID del lote en el sistema administrativo.
 * @param {string} token - JWT de la sesión actual del admin.
 * @returns {string|null} URL absoluta, o null si faltan datos.
 */
export const buildAdminLotUrl = (lotId, token) => {
  if (!lotId || !token) return null;
  const base =
    APP_CONFIG.ADMIN_APP_URLS[APP_CONFIG.ENV] ||
    APP_CONFIG.ADMIN_APP_URLS.production;
  const redirectUrl = encodeURIComponent(`/lots/${lotId}`);
  return `${base}/login?token=${encodeURIComponent(token)}&redirectUrl=${redirectUrl}`;
};

// Función para cambiar dinámicamente el entorno (útil para testing)
export const switchEnvironment = (environment) => {
  if (!["development", "qa", "production"].includes(environment)) {
    console.error("❌ Invalid environment:", environment);
    return false;
  }

  const baseUrl = APP_CONFIG.ENVIRONMENT_URLS[environment];
  console.log(`🔄 Switching to ${environment} environment:`, baseUrl);

  // Esta función es solo informativa, el cambio real requiere variables de entorno
  return true;
};

// Función de utilidad para obtener información del entorno actual
export const getEnvironmentInfo = () => {
  return {
    environment: APP_CONFIG.ENV,
    baseUrl: API_CONFIG.BASE_URL,
    version: APP_CONFIG.VERSION,
    name: APP_CONFIG.NAME,
    isDevelopment: APP_CONFIG.IS_DEVELOPMENT,
    isQA: APP_CONFIG.IS_QA,
    isProduction: APP_CONFIG.IS_PRODUCTION,
    availableEnvironments: Object.keys(APP_CONFIG.ENVIRONMENT_URLS),
  };
};

// Log de configuración en desarrollo y QA
if (APP_CONFIG.IS_DEVELOPMENT || APP_CONFIG.IS_QA) {
  const envInfo = getEnvironmentInfo();
  console.log(`🔧 API Configuration [${envInfo.environment.toUpperCase()}]:`, {
    baseUrl: envInfo.baseUrl,
    environment: envInfo.environment,
    version: envInfo.version,
    name: envInfo.name,
  });

  // Mostrar warning si estamos en QA
  if (APP_CONFIG.IS_QA) {
    console.log(
      "🧪 Running in QA environment - API calls will go to testing server"
    );
  }
}

export default API_CONFIG;
